import { motion } from 'framer-motion';
import moment from 'moment';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import CloseIcon from '~/assets/CloseIcon';
import NewInfoIcon from '~/assets/NewInfoIcon';
import { AiCreditsUsageQuery } from '~/types/api.graphql';

import {
  BannerContainer,
  BannerLeft,
  BannerRight,
  BannerText,
  BannerVariant,
  DismissButton,
  IconBadge,
  LearnMoreButton,
} from './SessionLimitBanner.styles';
import { SESSION_LIMIT_BANNER_THRESHOLD, SESSION_LIMIT_REACHED_THRESHOLD } from './sessionLimitDismissal';

export type UsageSummary = AiCreditsUsageQuery['aiCreditsUsage'];

interface SessionLimitBannerProps {
  usage: UsageSummary;
  onLearnMore: () => void;
  onDismiss: () => void;
}

// Wraps BannerContainer so the dismiss fade-out can be driven by framer-motion.
const MotionBannerContainer = motion(BannerContainer);

const getVariant = (percentage: number): BannerVariant | null => {
  if (percentage >= SESSION_LIMIT_REACHED_THRESHOLD) return 'amber';
  if (percentage >= SESSION_LIMIT_BANNER_THRESHOLD) return 'blue';
  return null;
};

const formatResetTime = (periodEnd: UsageSummary['periodEnd']): string | null => {
  if (!periodEnd) return null;
  const end = moment(String(periodEnd));
  if (!end.isValid()) return null;
  return end.format('h:mm a'); // e.g. "5:00 pm"
};

const SessionLimitBanner = ({ usage, onLearnMore, onDismiss }: SessionLimitBannerProps) => {
  const { t } = useTranslation();
  // Local flag so the banner can play its fade-out before the parent unmounts it.
  const [isDismissing, setIsDismissing] = useState(false);

  if (!usage.enabled) return null;

  const variant = getVariant(usage.percentage);
  if (!variant) return null;

  const resetTime = variant === 'amber' ? formatResetTime(usage.periodEnd) : null;

  const message =
    variant === 'amber'
      ? resetTime
        ? t('aiInsights.sessionLimitReached', { time: resetTime })
        : t('aiInsights.sessionLimitReachedNoTime')
      : t('aiInsights.sessionLimitWarning', { percentage: usage.percentage });

  return (
    <MotionBannerContainer
      $variant={variant}
      initial={false}
      // Collapse the banner's height (not just opacity) so the input below glides
      // up smoothly instead of snapping when it unmounts. marginBottom/padding
      // animate to 0 too since they're part of the footprint (incl. the notch).
      animate={
        isDismissing ? { opacity: 0, height: 0, marginBottom: 0, paddingTop: 0, paddingBottom: 0 } : { opacity: 1 }
      }
      transition={{ duration: 0.2, ease: 'easeOut' }}
      onAnimationComplete={() => isDismissing && onDismiss()}
      style={{ overflow: 'hidden' }}
    >
      <BannerLeft>
        <IconBadge $variant={variant}>
          <NewInfoIcon circleFill="currentColor" />
        </IconBadge>
        <BannerText>{message}</BannerText>
      </BannerLeft>
      <BannerRight>
        <LearnMoreButton type="button" onClick={onLearnMore}>
          {t('aiInsights.learnMore')}
        </LearnMoreButton>
        {variant === 'blue' && (
          <DismissButton type="button" onClick={() => setIsDismissing(true)} aria-label={t('common.close')}>
            <CloseIcon />
          </DismissButton>
        )}
      </BannerRight>
    </MotionBannerContainer>
  );
};

export default SessionLimitBanner;
